import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Users,
  Building2,
  Mail,
  Phone,
  Eye,
  Send,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableTableHead } from "../components/SortableTableHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(null);
  const { sortKey, sortDir, toggleSort, sortData } = useSortableTable();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    vat_number: "",
    address: {
      street: "",
      city: "",
      state: "",
      postal_code: "",
      country: "South Africa",
    },
    notes: "",
    reminder_days_before: 7,
  });

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        withCredentials: true,
      });
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        address: formData.address.street ? formData.address : null,
      };

      if (editingCustomer) {
        await axios.put(
          `${API}/customers/${editingCustomer.customer_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success("Customer updated successfully");
      } else {
        await axios.post(`${API}/customers`, payload, {
          withCredentials: true,
        });
        toast.success("Customer created successfully");
      }
      setSheetOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error(error.response?.data?.detail || "Failed to save customer");
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email,
      phone: customer.phone || "",
      company_name: customer.company_name || "",
      vat_number: customer.vat_number || "",
      address: customer.address || {
        street: "",
        city: "",
        state: "",
        postal_code: "",
        country: "South Africa",
      },
      notes: customer.notes || "",
      reminder_days_before: customer.reminder_days_before || 7,
    });
    setSheetOpen(true);
  };

  const handleDelete = async () => {
    if (!customerToDelete) return;
    try {
      await axios.delete(`${API}/customers/${customerToDelete.customer_id}`, {
        withCredentials: true,
      });
      toast.success("Customer deleted successfully");
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
      fetchCustomers();
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Failed to delete customer");
    }
  };

  const handleSendInvite = async (customer) => {
    setSendingInvite(customer.customer_id);
    try {
      await axios.post(
        `${API}/customers/${customer.customer_id}/send-registration-link`,
        {},
        { withCredentials: true }
      );
      toast.success(`Registration link sent to ${customer.email}`);
    } catch (error) {
      console.error("Error sending invite:", error);
      toast.error(
        error.response?.data?.detail || "Failed to send registration link"
      );
    } finally {
      setSendingInvite(null);
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      company_name: "",
      vat_number: "",
      address: {
        street: "",
        city: "",
        state: "",
        postal_code: "",
        country: "South Africa",
      },
      notes: "",
      reminder_days_before: 7,
    });
  };

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (customer.company_name &&
        customer.company_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const sortedCustomers = sortData(filteredCustomers, {
    customer: (c) => c.name,
    contact: (c) => c.email,
    company: (c) => c.company_name,
    status: (c) => (c.user_id ? "Registered" : "Not Registered"),
  });

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Customers
          </h1>
          <p className="text-slate-500 mt-1">
            Manage customer profiles and installations
          </p>
        </div>
        <Button
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
          onClick={() => setSheetOpen(true)}
          data-testid="add-customer-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-customers"
          />
        </div>
      </div>

      {/* Customers Table */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {sortedCustomers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead columnKey="customer" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Customer</SortableTableHead>
                  <SortableTableHead columnKey="contact" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Contact</SortableTableHead>
                  <SortableTableHead columnKey="company" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Company</SortableTableHead>
                  <SortableTableHead columnKey="status" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Status</SortableTableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCustomers.map((customer) => (
                  <TableRow 
                    key={customer.customer_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/customers/${customer.customer_id}`)}
                    data-testid={`customer-row-${customer.customer_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                          <span className="text-emerald-700 font-medium">
                            {customer.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          {customer.address?.city && (
                            <p className="text-sm text-slate-500">
                              {customer.address.city}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-3.5 w-3.5 text-slate-400" />
                          <span>{customer.email}</span>
                        </div>
                        {customer.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Phone className="h-3.5 w-3.5 text-slate-400" />
                            <span>{customer.phone}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {customer.company_name ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          <span>{customer.company_name}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {customer.user_id ? (
                        <Badge className="rounded-sm bg-emerald-50 text-emerald-700">
                          Registered
                        </Badge>
                      ) : (
                        <Badge className="rounded-sm bg-slate-100 text-slate-600">
                          Not Registered
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(`/customers/${customer.customer_id}`)
                            }
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(customer)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          {!customer.user_id && (
                            <DropdownMenuItem
                              onClick={() => handleSendInvite(customer)}
                              disabled={sendingInvite === customer.customer_id}
                            >
                              {sendingInvite === customer.customer_id ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Send className="mr-2 h-4 w-4" />
                              )}
                              Send Registration Link
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setCustomerToDelete(customer);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No customers found
              </h3>
              <p className="text-slate-500 mb-6">
                {searchQuery
                  ? "Try adjusting your search"
                  : "Add your first customer"}
              </p>
              {!searchQuery && (
                <Button
                  onClick={() => setSheetOpen(true)}
                  className="rounded-sm bg-slate-900 hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Sheet */}
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) resetForm();
        }}
      >
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {editingCustomer ? "Edit Customer" : "Add Customer"}
            </SheetTitle>
            <SheetDescription>
              {editingCustomer
                ? "Update customer information"
                : "Create a new customer profile"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Customer name"
                required
                className="rounded-sm"
                data-testid="customer-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="email@example.com"
                  required
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  placeholder="+27 XX XXX XXXX"
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) =>
                    setFormData({ ...formData, company_name: e.target.value })
                  }
                  placeholder="Company Pty Ltd"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vat_number">VAT Number</Label>
                <Input
                  id="vat_number"
                  value={formData.vat_number}
                  onChange={(e) =>
                    setFormData({ ...formData, vat_number: e.target.value })
                  }
                  placeholder="VAT number"
                  className="rounded-sm"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address.street}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value },
                  })
                }
                placeholder="Street address"
                className="rounded-sm mb-2"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={formData.address.city}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value },
                    })
                  }
                  placeholder="City"
                  className="rounded-sm"
                />
                <Input
                  value={formData.address.state}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, state: e.target.value },
                    })
                  }
                  placeholder="Province"
                  className="rounded-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={formData.address.postal_code}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: {
                        ...formData.address,
                        postal_code: e.target.value,
                      },
                    })
                  }
                  placeholder="Postal code"
                  className="rounded-sm"
                />
                <Input
                  value={formData.address.country}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, country: e.target.value },
                    })
                  }
                  placeholder="Country"
                  className="rounded-sm"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reminder_days">Reminder (days before service due)</Label>
              <Input
                id="reminder_days"
                type="number"
                min="1"
                max="30"
                value={formData.reminder_days_before}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    reminder_days_before: parseInt(e.target.value) || 7,
                  })
                }
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Additional notes"
                rows={2}
                className="rounded-sm"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSheetOpen(false);
                  resetForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="save-customer-btn"
              >
                {editingCustomer ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{customerToDelete?.name}"? This
              will also remove their installations and booking history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Customers;
